/* global iom */
/*
Piano Tracking
https://github.com/at-internet/piano-analytics-js?tab=readme-ov-file#using-npm
https://developers.atinternet-solutions.com/piano-analytics/data-collection/how-to-send-events/collection-api
*/

import URL from "./URL";
import {pianoAnalytics} from 'piano-analytics-js';

export default class Tracking
{
    constructor(config, router)
    {
        if (Tracking._instance)
        {
            return Tracking._instance
        }
        Tracking._instance = this;

        this.iam = false;
        this.router = router;
        this.config = Object.assign(config);
        /* Beispiel
        let config = {
            storyName: 'Name der 3D Story',
            storyID: '3D Story-ID',
        };
         */

        // Umgebung der Anwendung erkennen (web, HeuteApp iOS, HeuteApp Android)
        this.environment = 'web';
        // HeuteApp erkennen
        if (window.navigator.userAgent.indexOf('HeuteApp/') !== -1)
        {
            // iOS: Mozilla/5.0 (iPhone; CPU iPhone OS 13_3_1 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Mobile/15E148 HeuteApp/3.0 (de.zdf.heute; build:126; iOS 13.3.1)
            // Android: Mozilla/5.0 (Linux; Android 8.1.0; Nexus 6P Build/OPM7.181205.001; wv) AppleWebKit/537.36 (KHTML, like Gecko) Version/4.0 Chrome/80.0.3987.149 Mobile Safari/537.36 HeuteApp/3.0
            if ((window.navigator.userAgent.indexOf('iPhone') !== -1)
                || (window.navigator.userAgent.indexOf('iPad') !== -1)
                || (window.navigator.userAgent.indexOf('iPod') !== -1)
            )
            {
                this.environment = 'ios';
            }
            else
            {
                this.environment = 'android';
            }
        }

        const envValues = {
            web: {
                st: 'zdf',
                level1: '638798'
            },
            ios: {
                st: 'iOS',
                level1: '577758'
            },
            android: {
                st: 'Android',
                level1: '577765'
            }
        };

        // tracking.environment vom Parameter
        if (URL.hasQuery('env'))
        {
            this.environment = URL.getQuery('env');
        }

        this.env = envValues[this.environment];

        if (typeof this.config.storyName === 'undefined')
        {
            throw 'Name der 3D Story fehlt in der Tracking config (storyName)';
        }
        if (typeof this.config.storyID === 'undefined')
        {
            throw 'ID der 3D Story fehlt in der Tracking config (storyID)';
        }

        // Tracking deaktivieren für Tests
        if (config.disabled)
        {
            this.doNotTrack = true;

            if (process.env.NODE_ENV === 'development')
            {
                // eslint-disable-next-line no-console
                console.log('Babylon Story: Tracking is disabled.');
            }

            return;
        }

        // doNotTrack
        this.doNotTrack = window.navigator.userAgent.indexOf('notracking') > -1;
        if (this.doNotTrack)
        {
            if (process.env.NODE_ENV === 'development')
            {
                // eslint-disable-next-line no-console
                console.debug('Do not track is set - ignoring');
            }
        }

        this.updateConsentPromise();

        // LoadingTime
        // Wird von Footer.created() beendet
        this.loadingTimeStart = performance.now();
    }

    static instance()
    {
        if (!Tracking._instance)
        {
            throw new Error('Tracking singleton not created.');
        }

        return Tracking._instance;
    }

    updateConsentPromise()
    {
        this.consentPromise = new Promise((resolve) =>
        {
            if (this.environment !== 'web')
            {
                return resolve();
            }

            if (window.zdfcmp)
            {
                window.zdfcmp.addConsentChangedCallback(() =>
                {
                    if (window.zdfcmp.hasTrackingConsent())
                    {
                        return resolve();
                    }
                    else
                    {
                        this.updateConsentPromise();
                    }
                });
            }
            else
            {
                // eslint-disable-next-line no-console
                console.error('zdfcmp not found');
            }
        });
    }

    loadScript(url, scriptid)
    {
        let scriptEl = document.createElement('script');
        scriptEl.type = 'text/javascript';
        scriptEl.async = true;
        scriptEl.src = url;
        scriptEl.id = scriptid;

        document.getElementsByTagName('head')[0].appendChild(scriptEl);

        return new Promise((res) => {
            scriptEl.onload = function() {
                res();
            }
        });
    }

    loadPianoTracking()
    {
        if (!this.tag)
        {
            pianoAnalytics.setConfigurations({
                'site': this.env.level1,
                'collectDomain': 'https://logs1407.xiti.com',
            });
            this.tag = pianoAnalytics;
        }
    }

    loadIAM()
    {
        if (!this.iam)
        {
            this.iam = this.loadScript('https://script.ioam.de/iam.js', 'ivw');
        }

        return this.iam;
    }

    getPageName(route)
    {
        if (!route)
        {
            route = this.router.currentRoute.value;
        }

        if (route.meta && route.meta.title)
        {
            return route.meta.title;
        }

        return route.path;
    }

    page(route)
    {
        if (this.doNotTrack)
        {
            return;
        }

        let pageName = route.path;

        // SZM-Page Tag
        let iam_data = {
            "st": this.env.st,
            "cp": "Nachrichten/Stories",
            "co": "3d-tool" + "/" + pageName + '/' + this.config.storyID, // content type/page titel/Sophora-ID
            "sv": "ke",
            // MCVD method activated
            "sc": "yes", // ?
        };
        if (this.environment === 'android')
        {
            iam_data.st = 'aadheute';
        }
        else if (this.environment === 'ios')
        {
            iam_data.st = 'appheute';
        }

        // IAM Tracking
        this.consentPromise.then(() =>
        {
            this.loadIAM().then(() =>
            {
                if (typeof (iom) !== 'undefined')
                {
                    if (process.env.NODE_ENV === 'development')
                    {
                        // eslint-disable-next-line no-console
                        console.debug('track.page IAM', iam_data);
                    }

                    iom.c(iam_data, 1);
                }
            });
        });

        let data = this.getPageData(route);

        this.sendEvent('page.display', data);
    }

    getPageData(route)
    {
        let pageName = this.getPageName(route);

        return {
            "page": this.config.storyName,
            "page_title": this.config.storyName,

            "site_level1": this.env.level1,

            "site_level2": "15", // für Nachrichten
            "page_section": "15", // für Nachrichten

            "page_chapter1": "Stories",
            "page_subsection": "Stories",

            "page_chapter2": "3D-Tool",
            "page_type": "3D-Tool",

            "page_chapter3": this.config.storyName + '|' + pageName,
            "page_sophora_id": this.config.storyName + '|' + pageName,

            "contain_bildergalerie": false,
            "contain_wahltool": false,
            "publication_timestamp": (new Date()).toISOString(),
        };
    }

    sendEvent(name, data)
    {
        if (this.doNotTrack)
        {
            return;
        }

        this.consentPromise.then(() =>
        {
            this.loadPianoTracking();

            if (process.env.NODE_ENV === 'development')
            {
                // eslint-disable-next-line no-console
                console.debug('pianoAnalytics sendEvent ' + name, data);
                //console.trace();
            }

            pianoAnalytics.sendEvent(name, data);
        });
    }

    sendClickEvent(type, route, label, label_detail)
    {
        if (this.doNotTrack)
        {
            return;
        }

        const path = route.path;
        const pageName = this.getPageName(route);

        // ATI-Tagging
        let data = {
            ...this.getPageData(route),
            'click': pageName, // Titel der Seite, auf die verwiesen wird
            'target_title': pageName, // Titel der Seite, auf die verwiesen wird

            "click_chapter1": 'Stories', // Sendungsbereich der Seite, auf die verwiesen wird
            "target_subsection": 'Stories', // Sendungsbereich der Seite, auf die verwiesen wird

            "click_chapter2": "3D-Tool", // Inhaltstyp der Seite, auf die verwiesen wird
            "target_type": "3D-Tool", // Inhaltstyp der Seite, auf die verwiesen wird

            "click_chapter3": this.config.storyID, // Sophora-ID der Seite, auf die verwiesen wird
            "target_sophora_id": this.config.storyID, // Sophora-ID der Seite, auf die verwiesen wird

            "target_external_sophora_id": this.config.storyID, // External Sophora-ID der Seite, auf die verwiesen wird
            "target_section": '15', // Rubrik der Seite, auf die verwiesen wird
            "target_url": path, // URL der Seite, auf die verwiesen wird
        };

        if (label)
        {
            data["function"] = label;
        }
        if (label_detail)
        {
            data["function_detail"] = label_detail;
        }

        this.sendEvent(type, data);
    }

    navigationClick(targetRoute, label)
    {
        this.sendClickEvent('click.navigation', targetRoute, label);
    }

    actionClick(label, label_detail)
    {
        this.sendClickEvent('click.action', this.router.currentRoute.value, label, label_detail);
    }

    hotspotClick(label)
    {
        this.actionClick( 'hotspot_oeffnen', 'hotspot ' + label);
    }

    loadingTime()
    {
        // LoadingTime
        let loadingTime = (performance.now() - this.loadingTimeStart) * 0.001; // msec in sec
        this.sendEvent('page.load', {
            ...this.getPageData(this.router.currentRoute.value),
            global_loading_time: loadingTime + 'sec'
        });
    }
}